// AuthGate — wraps the whole app. Renders the login screen until a valid
// session exists, then renders children. Exposes the session + a logout via
// React context so the titlebar can show who's in and offer "sign out".

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import logoUrl from "../assets/bvndle-wordmark@2x.png";
import { attemptLogin, loadSession, logout, type Session } from "../lib/auth";

interface AuthCtx {
  session: Session | null;
  signOut: () => void;
}

const Ctx = createContext<AuthCtx>({ session: null, signOut: () => {} });

export function useAuth(): AuthCtx {
  return useContext(Ctx);
}

const ERROR_COPY: Record<string, string> = {
  "bad-email": "That doesn't look like an email address.",
  "not-allowed":
    "That email isn't on the access list. Ask Kabelo to add you, or use your @vfdgroup.com address.",
  "bad-passphrase": "Wrong passphrase.",
};

export function AuthGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setSession(loadSession());
    setReady(true);
  }, []);

  const signOut = useCallback(() => {
    logout();
    setSession(null);
  }, []);

  const value = useMemo<AuthCtx>(() => ({ session, signOut }), [session, signOut]);

  if (!ready) return null; // avoid a flash of the login screen on launch

  if (!session) {
    return <LoginScreen onSuccess={setSession} />;
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function LoginScreen({ onSuccess }: { onSuccess: (s: Session) => void }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const result = attemptLogin(email, pass);
      if (typeof result === "string") {
        setError(ERROR_COPY[result] ?? "Could not sign in.");
        return;
      }
      setError(null);
      onSuccess(result);
    },
    [email, pass, onSuccess],
  );

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={brandRow}>
          <img src={logoUrl} alt="Bvndle" style={logoImg} />
          <div style={brandSub}>COMMAND CENTRE</div>
        </div>
        <p style={ethos}>
          No surprise updates. No moving goalposts. No accountability gaps.
        </p>
        <p style={lede}>
          Every initiative carries the same{" "}
          <span style={ledeAccent}>five-field shape</span> · so the review surface speaks for
          itself. Sign in to begin.
        </p>
        <p style={placeholder}>
          Use your <b>@bvndle.com</b> or <b>@vfdgroup.com</b> email and the ManCo passphrase.
        </p>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={label}>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@vfdgroup.com"
              autoFocus
              style={input}
            />
          </label>
          <label style={label}>
            Passphrase
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="shared passphrase"
              style={input}
            />
          </label>
          {error && <div style={errBox}>{error}</div>}
          <button type="submit" style={button}>
            Enter →
          </button>
        </form>
        <div style={foot}>
          powered by <b style={{ color: "var(--ink-dim)" }}>VFD Group</b>
        </div>
      </div>
    </div>
  );
}

// — styles (inline so the gate doesn't depend on suite.css class names) —

const wrap: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "grid",
  placeItems: "center",
  background:
    "radial-gradient(ellipse at top left, rgba(122,63,176,0.14), transparent 52%)," +
    "radial-gradient(ellipse at bottom right, rgba(232,90,159,0.10), transparent 55%)," +
    "var(--bg-0)",
  zIndex: 1000,
};
const card: React.CSSProperties = {
  width: 380,
  maxWidth: "90vw",
  padding: "32px 30px",
  background: "var(--panel-2)",
  border: "1px solid var(--edge)",
  borderRadius: "var(--r-xl)",
  boxShadow: "var(--shadow-pop)",
};
const brandRow: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
  marginBottom: 20,
};
const logoImg: React.CSSProperties = {
  width: 168,
  height: "auto",
  display: "block",
  filter: "drop-shadow(0 0 14px rgba(232,90,159,0.35))",
};
const brandSub: React.CSSProperties = {
  fontSize: 9.5,
  letterSpacing: "0.28em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  fontFamily: "var(--font-mono)",
};
const ethos: React.CSSProperties = {
  fontSize: 13,
  color: "var(--ink)",
  lineHeight: 1.5,
  margin: "0 0 10px",
  fontWeight: 500,
};
const lede: React.CSSProperties = {
  fontSize: 12.5,
  color: "var(--ink-dim)",
  lineHeight: 1.55,
  margin: "0 0 14px",
};
const ledeAccent: React.CSSProperties = {
  color: "var(--pink)",
  fontStyle: "italic",
  fontWeight: 600,
};
const placeholder: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-faint)",
  lineHeight: 1.45,
  margin: "0 0 18px",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.02em",
};
const label: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontSize: 10,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  fontWeight: 700,
};
const input: React.CSSProperties = {
  background: "var(--bg-1)",
  border: "1px solid var(--edge)",
  borderRadius: "var(--r-sm)",
  padding: "10px 12px",
  color: "var(--ink)",
  fontSize: 13,
  fontFamily: "var(--font-display)",
  outline: "none",
};
const errBox: React.CSSProperties = {
  fontSize: 12,
  color: "var(--coral)",
  background: "var(--coral-soft)",
  border: "1px solid var(--coral)",
  borderRadius: "var(--r-sm)",
  padding: "8px 10px",
  lineHeight: 1.45,
};
const button: React.CSSProperties = {
  marginTop: 4,
  background: "linear-gradient(135deg, var(--mint), var(--teal-deep))",
  color: "var(--bg-0)",
  border: "none",
  borderRadius: "var(--r-sm)",
  padding: "11px 14px",
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  cursor: "pointer",
};
const foot: React.CSSProperties = {
  marginTop: 20,
  textAlign: "center",
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
};
